import React from 'react';
import isBlank from '../utilities/isBlank';
import isNonNegativeNumber from '../utilities/isNonNegativeNumber';
import currencySym from 'currency-symbol-map';
import _ from 'lodash';
import validate from 'validate.js';
import { FormattedMessage } from 'react-intl';

const validateCreateItemForm = (values, props) => {
  const { currency } = props;
  const currencySymbol = currencySym(currency);
  const {
    category,
    subcategory,
    label,
    unit_cost,
    unit_cost_basis,
    visible_on_listing,
    description,
    taxes = [],
    fees = [],
    count
  } = values;

  const addOnNameErrorMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_name_error_message" defaultMessage="Please give your add-on a name" />;
  const addOnNameMinCharMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_name_min_character_message" defaultMessage="Please enter at least 5 characters" />;
  const addOnDescriptionErrorMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_description_error_message" defaultMessage="Please enter a description" />;
  const addOnDescriptionMinCharMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_description_min_character_message" defaultMessage="Please enter at least 10 characters"/>;
  const addOnDescriptionMaxCharMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_description_max_character_message" defaultMessage="The description is too long (2000 character limit)"/>;
  const addOnSelectionErrorMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_selection_error_message" defaultMessage="Please make a selection" />;
  const addOnUnitCostErrorMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_unit_cost_error_message" defaultMessage="Please enter a price" />;
  const addOnUnitCostMinPriceMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_unit_cost_min_price_message" defaultMessage="Enter a price less than {currencySymbol}999,999" values={{ currencySymbol: currencySymbol }}/>;
  const addOnUnitCostInvalidMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_unit_cost_invalid_message" defaultMessage="Invalid" />;
  const addOnMinQuantityMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_min_quantity_message" defaultMessage="Enter a quantity less than 9,999" />;
  const addOnMaxQuantityMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_max_quantity_message" defaultMessage="Enter a quantity greater than 0" />;
  const addOnAmountMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_amount_message" defaultMessage="Please enter an amount" />;
  const addOnMinTaxMessage = <FormattedMessage id="hosting.create_item_form.tax_validation_error_text" defaultMessage="Enter the % as digits only. Number must be less than 35." />;
  const addOnMaxTaxMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_max_tax_message" defaultMessage="Maximum of 5 taxes" />;
  const addOnMinFeeMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_min_fee_message" defaultMessage="Enter a fee less than 50%" />;
  const addOnMinFeeAmountMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_min_fee_amount_message" defaultMessage="Enter a fee less than {currencySymbol}99,999" values={{ currencySymbol: currencySymbol }} />;
  const addOnMaxFeeMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_max_fee_message" defaultMessage="Maximum of 5 fees" />;
  const addOnIntegerMessage = <FormattedMessage id="hosting.validate_create_item_form.add_on_integer_message" defaultMessage="Please enter a whole number" />;

  const errors = {
    category: validate.single(category, {
      presence: { allowEmpty: false }
    }),
    subcategory: validate.single(subcategory, {
      presence: { allowEmpty: false }
    }),
    label: validate.single(label, {
      presence: { allowEmpty: false, message: addOnNameErrorMessage },
      length: {
        minimum: 5,
        tooShort: addOnNameMinCharMessage
      }
    }),
    description: validate.single(description, {
      presence: { allowEmpty: false, message: addOnDescriptionErrorMessage },
      length: {
        minimum: 10,
        maximum: 2000,
        tooShort: addOnDescriptionMinCharMessage,
        tooLong: addOnDescriptionMaxCharMessage
      }
    }),
    visible_on_listing: validate.single(visible_on_listing, {
      presence: { allowEmpty: false, message: addOnSelectionErrorMessage }
    }),
    unit_cost: 999999 < Number(unit_cost) ? addOnUnitCostMinPriceMessage : isBlank(unit_cost, addOnUnitCostErrorMessage) || isNonNegativeNumber(unit_cost, addOnUnitCostInvalidMessage),
    unit_cost_basis: validate.single(unit_cost_basis, {
      presence: { allowEmpty: false }
    }),
    count: validate.single(count || null, {
      presence: false,
      numericality: {
        onlyInteger: true,
        notInteger: addOnIntegerMessage,
        lessThan: 9999,
        notLessThan: addOnMinQuantityMessage,
        greaterThan: 0,
        notGreaterThan: addOnMaxQuantityMessage
      }
    })
  };

  const taxesArrayErrors = [];
  _.map(taxes, (tax, i) => {
    const taxErrors = {};
    if (!tax.label) {
      taxErrors.label = true;
    }
    if (!tax.amount) {
      taxErrors.amount = addOnAmountMessage;
    }
    if (35 < Number(tax.amount)) {
      taxErrors.amount = addOnMinTaxMessage;
    }
    if (4 < i) {
      taxErrors.amount = addOnMaxTaxMessage;
    }
    taxesArrayErrors[i] = taxErrors;
    errors.taxes = taxesArrayErrors;
  });

  const feesArrayErrors = [];
  _.map(fees, (fee, i) => {
    const feeErrors = {};
    if (!fee.label) {
      feeErrors.label = true;
    }
    if (!fee.amount) {
      feeErrors.amount = addOnAmountMessage;
    }
    if (50 < Number(fee.amount) && '%' === fee.charge_unit) {
      feeErrors.amount = addOnMinFeeMessage;
    }
    if (99999 < Number(fee.amount) && '$' === fee.charge_unit) {
      feeErrors.amount = addOnMinFeeAmountMessage;
    }
    if (4 < i) {
      feeErrors.amount = addOnMaxFeeMessage;
    }
    feesArrayErrors[i] = feeErrors;
    errors.fees = feesArrayErrors;
  });

  return errors;
};

export default validateCreateItemForm;
